/**
 * Helpers for rendering Deck Template (recipe) data on the client.
 *
 * Backend ships `category` as a populated object whenever possible (`{ id, slug,
 * name, meta, ... }`). When the API returns just an ObjectId string, join it
 * against `deckTemplateCategoriesApi.list()` via `enrichTemplateCategory`.
 */

// ──────────────────────────────────────────────────────────────────────────
// Modality / column-type icon + color metadata.
// Used by the recipe index card, the recipe detail step list, and the
// deck-mockup empty state. Keep these aligned with backend
// `DeckTemplate.columns[].modality` and `DeckTemplate.columns[].type` values.
// ──────────────────────────────────────────────────────────────────────────
export const MODALITY_META = {
  chat:         { icon: 'message',                            color: '#3B82F6', label: 'Chat',       longLabel: 'Chat / LLM'         },
  llm:          { icon: 'cube',                               color: '#3B82F6', label: 'LLM',        longLabel: 'Text LLM'           },
  image:        { icon: 'image',                              color: '#A855F7', label: 'Image',      longLabel: 'Image model'        },
  video:        { icon: 'film',                               color: '#F472B6', label: 'Video',      longLabel: 'Video model'        },
  music:        { icon: 'music',                              color: '#10B981', label: 'Music',      longLabel: 'Music model'        },
  voice:        { icon: 'microphone',                         color: '#F59E0B', label: 'Voice',      longLabel: 'Voice / TTS model'  },
  transcribe:   { icon: 'waveform-lines',                     color: '#06B6D4', label: 'Transcribe', longLabel: 'Transcription model'},
  upscale:      { icon: 'up-right-and-down-left-from-center', color: '#EF4444', label: 'Upscale',    longLabel: 'Upscale tool'       },
  avatar:       { icon: 'user-tie',                           color: '#8B5CF6', label: 'Avatar',     longLabel: 'Avatar model'       },
  sfx:          { icon: 'volume-high',                        color: '#14B8A6', label: 'SFX',        longLabel: 'Sound effects model'},
  '3d':         { icon: 'cubes-stacked',                      color: '#F97316', label: '3D',         longLabel: '3D model'           },
  threeD:       { icon: 'cubes-stacked',                      color: '#F97316', label: '3D',         longLabel: '3D model'           },
  tool:         { icon: 'wand-magic-sparkles',                color: '#6366F1', label: 'Tool',       longLabel: 'One-click tool'     },
  conversation: { icon: 'message',                            color: '#3B82F6', label: 'Chat',       longLabel: 'Chat / LLM'         },
  model:        { icon: 'cube',                               color: '#A855F7', label: 'Model',      longLabel: 'Model'              },
  agent:        { icon: 'user-astronaut',                     color: '#10B981', label: 'Agent',      longLabel: 'Custom AI agent'    },
};

const FALLBACK_MODALITY_META = MODALITY_META.tool;

/** Resolve a column's modality metadata, falling back to type, then `tool`. */
export function getColumnMeta(column) {
  if (!column) return FALLBACK_MODALITY_META;
  if (column.modality && MODALITY_META[column.modality]) return MODALITY_META[column.modality];
  if (column.type && MODALITY_META[column.type]) return MODALITY_META[column.type];
  return FALLBACK_MODALITY_META;
}

// ──────────────────────────────────────────────────────────────────────────
// Column bindings — model / tool / agent slugs ship on the template column.
// These helpers read both shapes the seed has used historically:
//   - flat  `column.bindModelSlug` (current `DeckTemplate.columns[]` schema)
//   - nested `column.bind?.modelSlug` (raw seed JSON shape)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Returns the bound resource kind ('model' | 'tool' | 'agent') or `null`
 * if the column doesn't ship with a concrete binding.
 */
export function getColumnBindKind(column) {
  if (!column) return null;
  if (column.bindModelSlug || column.bind?.modelSlug) return 'model';
  if (column.bindToolSlug || column.bind?.toolSlug) return 'tool';
  if (column.bindAgentSlug || column.bind?.agentSlug) return 'agent';
  return null;
}

/** Raw bound slug for the column (lowercase-dashed), or `null`. */
export function getColumnBindSlug(column) {
  if (!column) return null;
  return (
    column.bindModelSlug ||
    column.bindToolSlug ||
    column.bindAgentSlug ||
    column.bind?.modelSlug ||
    column.bind?.toolSlug ||
    column.bind?.agentSlug ||
    null
  );
}

/**
 * Human-readable label for a bound model/tool/agent.
 * Falls back to a title-cased version of the slug when no display name is
 * resolvable client-side (seed slugs are author-stable; titles ship from the
 * actual catalog only after the deck is cloned).
 */
export function getColumnBindLabel(column) {
  const slug = getColumnBindSlug(column);
  if (!slug) return null;
  return prettifyBindSlug(slug);
}

const BIND_SLUG_FORCE_CASE = {
  gpt: 'GPT',
  api: 'API',
  ai: 'AI',
  ml: 'ML',
  llm: 'LLM',
  tts: 'TTS',
  stt: 'STT',
  sfx: 'SFX',
  pbr: 'PBR',
  glb: 'GLB',
  fbx: 'FBX',
  '3d': '3D',
  '2d': '2D',
  hd:  'HD',
  uhd: 'UHD',
  ui:  'UI',
  ux:  'UX',
  io:  'IO',
};

function prettifyBindSlug(slug) {
  const parts = String(slug || '').toLowerCase().split('-').filter(Boolean);
  return parts
    .map((p) => {
      if (BIND_SLUG_FORCE_CASE[p]) return BIND_SLUG_FORCE_CASE[p];
      // Keep version-like tokens lowercase (`v6`, `v2`, `3d`) but capitalise alphas.
      if (/^v\d+/.test(p)) return p;
      if (/^\d/.test(p)) return p;
      return p.charAt(0).toUpperCase() + p.slice(1);
    })
    .join(' ');
}

// ──────────────────────────────────────────────────────────────────────────
// Column subtitle — one-line "what this column does" copy, surfaced on the
// recipe detail page and inside the cloned-deck empty state.
//
// Order of preference:
//   1. explicit `column.subtitle` from the seed JSON
//   2. auto-fallback built from modality + bound resource so existing recipes
//      still render *something* informative without backfilling every row.
// ──────────────────────────────────────────────────────────────────────────

/** "image" + "imagen-4-0" → "Generate images with Imagen 4 0." */
function autoColumnSubtitle(column) {
  const m = getColumnMeta(column);
  const kind = getColumnBindKind(column);
  const bindLabel = getColumnBindLabel(column);

  // verbs for each modality
  const verbs = {
    chat:         'Plan and brief in chat',
    conversation: 'Plan and brief in chat',
    llm:          'Plan and brief in text',
    image:        'Generate images',
    video:        'Generate video clips',
    music:        'Generate music',
    voice:        'Generate voiceover',
    transcribe:   'Transcribe audio',
    avatar:       'Generate a presenter avatar',
    sfx:          'Generate sound effects',
    '3d':         'Generate a 3D mesh',
    threeD:       'Generate a 3D mesh',
    upscale:      'Upscale assets',
    tool:         'Run a one-click tool',
    agent:        'Run a custom AI agent',
    model:        'Run a model',
  };
  const baseVerb = verbs[column?.modality] || verbs[column?.type] || `Run a ${m.label.toLowerCase()} column`;

  if (kind === 'model' && bindLabel) return `${baseVerb} with ${bindLabel}.`;
  if (kind === 'tool'  && bindLabel) return `Run the ${bindLabel} tool on the previous column.`;
  if (kind === 'agent' && bindLabel) return `Hand the brief to the ${bindLabel} agent.`;
  return `${baseVerb}.`;
}

export function getColumnSubtitle(column) {
  if (!column) return '';
  const explicit = typeof column.subtitle === 'string' ? column.subtitle.trim() : '';
  if (explicit) return explicit;
  return autoColumnSubtitle(column);
}

// ──────────────────────────────────────────────────────────────────────────
// Category resolution / labelling
// ──────────────────────────────────────────────────────────────────────────

export function isMongoIdString(s) {
  return typeof s === 'string' && /^[a-f0-9]{24}$/i.test(s.trim());
}

/** Seed filler slugs follow `{category}-column-pack-{n}` — extract the prefix. */
export function inferCategorySlugFromRecipeSlug(recipeSlug) {
  const s = String(recipeSlug || '').trim().toLowerCase();
  const m = s.match(/^(.+)-column-pack-\d+$/);
  return m ? m[1] : null;
}

export function buildCategoryLookupMaps(categories) {
  const byId = new Map();
  const bySlug = new Map();
  for (const cat of categories || []) {
    if (!cat || typeof cat !== 'object') continue;
    if (cat._id != null) byId.set(String(cat._id).toLowerCase(), cat);
    if (cat.slug) bySlug.set(String(cat.slug).toLowerCase(), cat);
  }
  return { byId, bySlug };
}

/**
 * Replace an unpopulated `template.category` (ObjectId string or `{ _id }`)
 * with a full populated category object, using the lookup maps. Falls back to
 * inferring the category from the recipe slug pattern when needed.
 *
 * Returns the original template if no improvement is possible.
 *
 * @param {object} template
 * @param {{ byId: Map<string, object>, bySlug: Map<string, object> }} maps
 */
export function enrichTemplateCategory(template, maps) {
  const { byId = new Map(), bySlug = new Map() } = maps || {};
  const c = template?.category;

  if (c && typeof c === 'object') {
    if (c.name || c.slug) {
      return template;
    }
    if (c._id != null) {
      const hit = byId.get(String(c._id).toLowerCase());
      if (hit) return { ...template, category: hit };
    }
  }

  if (typeof c === 'string' && c.trim()) {
    const s = c.trim().toLowerCase();
    if (isMongoIdString(c)) {
      const hit = byId.get(s);
      return hit ? { ...template, category: hit } : { ...template, category: null };
    }
    const hit = bySlug.get(s);
    if (hit) return { ...template, category: hit };
  }

  const inferred = inferCategorySlugFromRecipeSlug(template?.slug);
  if (inferred) {
    const hit = bySlug.get(inferred);
    if (hit) return { ...template, category: hit };
  }

  return template;
}

/**
 * Best-effort slug for grouping/routing. Preferred order:
 *   1. populated `category.slug`
 *   2. legacy string slug (`category` is a non-ObjectId string)
 *   3. inferred from recipe slug suffix `*-column-pack-N`
 *   4. `fallbackSlug`, else `'other'`
 */
export function recipeCategorySlug(template, fallbackSlug) {
  const c = template?.category;
  if (c && typeof c === 'object' && c.slug) return String(c.slug).toLowerCase();
  if (typeof c === 'string' && c.trim()) {
    if (isMongoIdString(c)) {
      return fallbackSlug ? String(fallbackSlug).toLowerCase() : 'other';
    }
    return c.trim().toLowerCase();
  }
  const inferred = inferCategorySlugFromRecipeSlug(template?.slug);
  if (inferred) return inferred;
  return fallbackSlug ? String(fallbackSlug).toLowerCase() : 'other';
}

function slugToTitle(slug) {
  return String(slug || '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Display label for the small "WORKFLOW · CATEGORY" eyebrow on a card.
 * Always returns an UPPERCASE string; never an ObjectId.
 *
 * @param {object} template
 * @param {string} [fallbackSlug] — section slug used when the ref is unpopulated
 */
export function formatRecipeCategoryLabel(template, fallbackSlug) {
  const c = template?.category;

  if (c && typeof c === 'object') {
    if (typeof c.name === 'string' && c.name.trim()) return c.name.replace(/\s+/g, ' ').toUpperCase();
    if (typeof c.slug === 'string' && c.slug.trim()) return slugToTitle(c.slug).toUpperCase();
    if (typeof c.id === 'string' && c.id.trim() && !isMongoIdString(c.id)) {
      return slugToTitle(c.id).toUpperCase();
    }
  }

  if (typeof c === 'string' && c.trim() && !isMongoIdString(c)) {
    return slugToTitle(c).toUpperCase();
  }

  if (fallbackSlug) return slugToTitle(fallbackSlug).toUpperCase();

  const inferred = inferCategorySlugFromRecipeSlug(template?.slug);
  if (inferred) return slugToTitle(inferred).toUpperCase();

  return 'WORKFLOW';
}
